import { useEffect, useState } from "react";
import { formatRelativeTimestamp } from "../utils/formatTime";
import { Tooltip } from "./shared/Tooltip";

interface RelativeTimestampProps {
  isoTimestamp: string | null | undefined;
  /**
   * If provided, the component will update the relative time every interval milliseconds.
   * Pass undefined or a high value if you don't need live updates (e.g., for "last completed" times).
   * Default: 60000 (1 minute) for reasonable accuracy without excessive re-renders.
   */
  updateIntervalMs?: number;
  /**
   * CSS class to apply to the wrapper span.
   */
  className?: string;
  /**
   * If true, shows the absolute time in a styled tooltip.
   * Default: true
   */
  showTimeAsTooltip?: boolean;
}

/**
 * Renders an ISO timestamp as relative time (e.g., "2 minutes ago") with the user's local timezone.
 * Automatically updates every minute to keep the relative time current.
 *
 * @example
 * <RelativeTimestamp isoTimestamp="2025-01-15T10:30:00Z" />
 * // renders: "2 minutes ago" (and updates every minute)
 *
 * <RelativeTimestamp
 *   isoTimestamp="2025-01-15T10:30:00Z"
 *   updateIntervalMs={undefined}
 *   showTimeAsTooltip={true}
 * />
 * // renders: "2 minutes ago" with a tooltip showing "15 Jan 2025, 10:30 AM"
 */
export function RelativeTimestamp({
  isoTimestamp,
  updateIntervalMs = 60000,
  className,
  showTimeAsTooltip = true,
}: RelativeTimestampProps): React.JSX.Element {
  const [displayText, setDisplayText] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<string | undefined>(undefined);

  // Format and update the display text
  useEffect(() => {
    const updateDisplay = () => {
      const formatted = formatRelativeTimestamp(isoTimestamp);
      if (formatted) {
        setDisplayText(formatted.relativeTime);
        setTooltip(showTimeAsTooltip ? formatted.absoluteTime : undefined);
      } else {
        setDisplayText(null);
        setTooltip(undefined);
      }
    };

    updateDisplay();

    // Only set interval if updateIntervalMs is defined and > 0
    if (updateIntervalMs && updateIntervalMs > 0) {
      const interval = setInterval(updateDisplay, updateIntervalMs);
      return () => clearInterval(interval);
    }
  }, [isoTimestamp, updateIntervalMs, showTimeAsTooltip]);

  if (!displayText) {
    return <span className={className}>{""}</span>;
  }

  return (
    <Tooltip label={tooltip}>
      <span className={className}>{displayText}</span>
    </Tooltip>
  );
}
